// noinspection DuplicatedCode
// Basiert auf https://gitlab.pooliestudios.com/koeln-marathon/app/app-dev

import addSeconds from 'date-fns/addSeconds';
import isAfter from 'date-fns/isAfter';
import parseISO from 'date-fns/parseISO';
import {Runner, RunnerInterface} from '../models/runner';
import RunProvider from './run';
import SearchProvider from './search';
import ApiProvider from './api';
import {RUNS} from '../models/waypoint';
import initRunnersStore, {RunnersStore} from '../store/runners';

export interface RunnersDetailResponse {
    nextUpdate?: string;
    data: Array<Runner>;
}

export interface RunnersListResponse {
    nextUpdate?: string;
    data: Array<RunnerInterface>;
}

export default class RunnersProvider {
    static instance = null;

    runners = new Map<number, Map<string, Map<string, Runner>>>();

    nextUpdate = new Map<string, Date>();

    private apiProvider: ApiProvider;

    private searchProvider: SearchProvider;

    private runProvider: RunProvider;

    private runnersStore: RunnersStore;

    private subscribeRunnersTimeout: NodeJS.Timeout;

    static getInstance(): RunnersProvider {
        if (!this.instance) {
            this.instance = new RunnersProvider();
        }

        return this.instance;
    }

    constructor() {
        this.apiProvider = new ApiProvider();
        this.searchProvider = SearchProvider.getInstance();
        this.runProvider = RunProvider.getInstance();
        this.runProvider.getYears()
            .forEach((year) => {
                const runs: Map<string, Map<string, Runner>> = new Map();

                RUNS.forEach((run) => {
                    runs.set(run, new Map());
                });

                this.runners.set(year, runs);
            });
        this.runnersStore = initRunnersStore();
    }

    subscribe(runners: Array<Runner>) {
        if (this.subscribeRunnersTimeout) {
            clearTimeout(this.subscribeRunnersTimeout);
        }

        if (!runners || runners.length === 0) return;
        if (runners.every((runner) => runner.hasFinished())) return;

        this.fetchUpdateRunners(runners)
            .then(() => {
                this.subscribeRunnersTimeout = setTimeout(() => {
                    this.subscribe(runners);
                }, 1000);
            });
    }

    unsubscribe() {
        if (this.subscribeRunnersTimeout) {
            clearTimeout(this.subscribeRunnersTimeout);
        }
    }

    hasRunner(runnerInterface: RunnerInterface): boolean {
        const yearMap = this.runners.get(runnerInterface.year);
        if (!yearMap) return false;
        
        const runMap = yearMap.get(runnerInterface.run);
        if (!runMap) return false;
        
        return runMap.has(runnerInterface.startNumber);
    }

    private getRunnerFromMap(runnerInterface: RunnerInterface): Runner | null {
        if (runnerInterface && runnerInterface.year && runnerInterface.run && runnerInterface.startNumber) {
            const yearMap = this.runners.get(runnerInterface.year);
            if (!yearMap) return null;
            
            const runMap = yearMap.get(runnerInterface.run);
            if (!runMap) return null;
            
            return runMap.get(runnerInterface.startNumber) || null;
        }

        return null;
    }

    async getRunners(runnerInterfaces: RunnerInterface[]): Promise<Runner[]> {
        await this.fetchUpdateRunners(runnerInterfaces);

        return runnerInterfaces
            .map((runnerInterface) => this.getRunnerFromMap(runnerInterface))
            .filter(Boolean);
    }

    setNextUpdateForRunners(runnerInterfaces: RunnerInterface[], nextUpdate: Date) {
        runnerInterfaces.forEach((runnerInterface) => {
            this.nextUpdate.set(Runner.getUuid(runnerInterface), nextUpdate);
        });
    }

    async fetchUpdateRunners(runnerInterfaces: RunnerInterface[]): Promise<void> {
        const years = {};
        runnerInterfaces.forEach((runnerInterface) => {
            if (!years[runnerInterface.year]) {
                years[runnerInterface.year] = [];
            }

            years[runnerInterface.year].push(runnerInterface);
        });

        await Promise.all(
            Object.keys(years)
                .map((year) => this.fetchUpdateRunnersSameYear(years[year])),
        );
    }

    static getNextUpdateFromResult(result: {nextUpdate?: string}): Date {
        const oneMinuteFromNow: Date = addSeconds(new Date(), 60);

        if (result.nextUpdate) {
            return new Date(
                Math.max.apply(null, [parseISO(result.nextUpdate), oneMinuteFromNow]),
            );
        }

        return oneMinuteFromNow;
    }

    async fetchUpdateRunnersSameYear(runnersInterfaces: Array<RunnerInterface | Runner>): Promise<void> {
        const {year} = runnersInterfaces[0];

        if (runnersInterfaces.every((runner) => runner instanceof Runner && runner.hasFinished())) {
            return;
        }

        const startNumbers = runnersInterfaces.map(({startNumber}) => startNumber);
        const nextUpdateRunners = runnersInterfaces.map(
            (runnerInterface) => this.nextUpdate.get(Runner.getUuid(runnerInterface)),
        );

        // Finde den nächstliegenden nextUpdate Wert aller angefragter Läufer
        const minNextUpdate = nextUpdateRunners.reduce((a, b) => (a < b ? a : b));
        if (nextUpdateRunners.every(Boolean) && isAfter(minNextUpdate, new Date())) {
            // eslint-disable-next-line no-console
            console.debug(`🆗 providers/runners: fetchUpdateRunners(${year}, [${startNumbers.toString()}])`);
            return;
        }

        // eslint-disable-next-line no-console
        console.log(`⬇️ providers/runners: fetchUpdateRunners(${year}, [${startNumbers.toString()}])`);

        try {
            this.setNextUpdateForRunners(
                runnersInterfaces,
                addSeconds(new Date(), 60),
            );

            const result = await this.fetchRunnersDetail(
                year,
                startNumbers,
            );

            this.setNextUpdateForRunners(
                runnersInterfaces,
                RunnersProvider.getNextUpdateFromResult(result),
            );

            const parsedRunners = this.parseRunnersData(result.data);

            this.runnersStore.setFavoriteRunners(parsedRunners);

            // Aktuelle Daten als Backup in der DB ablegen
            result.data.forEach((runnerData) => {
                window.localStorage.setItem(
                    `runners.${runnerData.year}.${runnerData.startNumber}`,
                    JSON.stringify(runnerData),
                );
            });
        } catch {
            runnersInterfaces.forEach((runnerInterface) => {
                // Wenn der Läufer noch nicht existiert versuchen Backup aus DB einzuspielen
                if (!this.hasRunner(runnerInterface)) {
                    const runnerData = window.localStorage.getItem(
                        `runners.${runnerInterface.year}.${runnerInterface.startNumber}`,
                    );
                    this.parseRunnersData([JSON.parse(runnerData)], false);
                }
            });

            // Erneut versuchen in 15 Sekunden
            this.setNextUpdateForRunners(
                runnersInterfaces,
                addSeconds(new Date(), 15),
            );
        }
    }

    async fetchRunnersDetail(year: number, startNumbers: Array<string>) {
        return this.apiProvider.getData<RunnersDetailResponse>(year, `runners/${startNumbers.join(',')}`);
    }

    async fetchRunnersList(year: number): Promise<void> {
        const nextUpdateRunners: Date = this.nextUpdate.get(`${year}-runners`);
        if (nextUpdateRunners && isAfter(nextUpdateRunners, new Date())) {
            // eslint-disable-next-line no-console
            console.debug(`🆗 providers/runners: fetchUpdateRunnersList(${year}) is up-to-date`);
            return Promise.resolve();
        }

        // eslint-disable-next-line no-console
        console.log(`⬇️ providers/runners: fetchUpdateRunnersList(${year})`);

        try {
            const result = await this.apiProvider.getData<RunnersListResponse>(year, 'runners');
            if (!result || !Object.values(result).length) {
                return Promise.reject();
            }

            this.nextUpdate.set(`${year}-runners`, RunnersProvider.getNextUpdateFromResult(result));

            this.parseRunnersData(result.data);
            window.localStorage.setItem(`runners.${year}`, JSON.stringify(result.data));
        } catch {
            // Backup laden
            const runnersFromStorage: Array<RunnerInterface> = JSON.parse(
                window.localStorage.getItem(`runners.${year}`) ?? '[]',
            );
            const sizeOfCachedRunners: number = Array.from(this.runners.get(year)
                .values())
                .reduce((sum: number, runMap) => sum + runMap.size, 0);

            if (runnersFromStorage && sizeOfCachedRunners < runnersFromStorage.length) {
                // Backup einspielen wenn vorhanden
                this.parseRunnersData(runnersFromStorage, false);
                // Sofort erlauben erneut zu versuchen
                this.nextUpdate.set(`${year}-runners`, new Date());
            } else {
                // Erneut versuchen in 15 Sekunden
                this.nextUpdate.set(`${year}-runners`, addSeconds(new Date(), 15));
            }
        }

        return Promise.resolve();
    }

    parseRunnersData(runnerInterfaces: Array<RunnerInterface>, assignToExisting: boolean = true): Array<Runner> {
        if (!runnerInterfaces) return [];

        return runnerInterfaces
            .filter((runner) => runner && runner.year && runner.startNumber)
            .map((runnerInterface) => {
                const runner = this.getRunnerFromMap({
                    year: runnerInterface.year,
                    run: runnerInterface.run,
                    startNumber: runnerInterface.startNumber,
                });

                if (runner && assignToExisting) {
                    runner.assign(runnerInterface);
                    return runner;
                }

                const newRunner = new Runner(runnerInterface);
                const yearMap = this.runners.get(newRunner.year);
                if (yearMap) {
                    const runMap = yearMap.get(newRunner.run);
                    if (runMap) {
                        runMap.set(newRunner.startNumber, newRunner);
                    }
                }

                this.searchProvider.addRunner(newRunner);
                return newRunner;
            });
    }
}
